import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchGql } from '@/lib/graphql_client'
import {
  CrmProposalCatalogueOptionsDocument,
  CrmProposalWizardDocument,
  CrmSaveProposalStepDocument,
  CrmSubmitProposalInputDocument,
  type CrmProposalInputSubmissionFieldsFragment,
  type CrmProposalWizardFieldsFragment,
  type CrmWizardSaveMode,
} from '@/gql/graphql'
import { useContextStore } from '@/lib/session/context-store'
import { proposalKeys } from './use-proposals'
import type { CatalogueKind } from './wizard-model'

/**
 * Data access for the proposal wizard (crm-svc T19). The wizard view is the editor's single source
 * of truth: a save returns the new view (version incremented), which replaces the cached one, so the
 * next save quotes the version the server just gave.
 *
 * Personal data: a save on the basics step may carry the client's contact. The variables are never
 * put in a query key, and the wizard view itself carries none.
 */

export type WizardView = CrmProposalWizardFieldsFragment
export type ProposalSubmission = CrmProposalInputSubmissionFieldsFragment

const useTenantId = () => useContextStore((s) => s.active?.tenantId)

const wizardKey = (tenantId: string | undefined, id: string) => [...proposalKeys.root(tenantId), 'wizard', id] as const

export function useProposalWizard(proposalId: string) {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: wizardKey(tenantId, proposalId),
    queryFn: async () => (await fetchGql(CrmProposalWizardDocument, { proposalId })).crmProposalWizard,
    retry: false,
    // An open editor is never refetched behind the user's back: a newer version arrives as a
    // CRM_VERSION_CONFLICT on save, which the page turns into an explicit reload.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

export interface SaveStepInput {
  proposalId: string
  stepKey: string
  values: Record<string, unknown>
  expectedVersion: number
  mode: CrmWizardSaveMode
  confirmCascadeReset: boolean
}

export function useSaveProposalStep() {
  const queryClient = useQueryClient()
  const tenantId = useTenantId()
  return useMutation({
    mutationFn: async (input: SaveStepInput) => (await fetchGql(CrmSaveProposalStepDocument, input)).crmSaveProposalStep,
    onSuccess: (view) => {
      queryClient.setQueryData(wizardKey(tenantId, view.proposalId), view)
      // The list and detail show state and version, which a save changes.
      void queryClient.invalidateQueries({ queryKey: [...proposalKeys.root(tenantId), 'list'] })
      void queryClient.invalidateQueries({ queryKey: proposalKeys.detail(tenantId, view.proposalId) })
      // A basics save may have created an append-only contact revision. Refresh it only when an
      // explicit reveal/edit action still has the audited contact query active.
      void queryClient.invalidateQueries({ queryKey: proposalKeys.contact(tenantId, view.proposalId) })
    },
  })
}

export interface SubmitInput {
  proposalId: string
  expectedVersion: number
  requestId: string
}

/**
 * Lock the input (crm-svc T20). The answer holds the server totals and the locked version's identity;
 * it stays in the mutation's memory only (no query key, no storage), because it is filled with the
 * proposal's own values.
 */
export function useSubmitProposalInput() {
  const queryClient = useQueryClient()
  const tenantId = useTenantId()
  return useMutation({
    mutationFn: async (input: SubmitInput) => (await fetchGql(CrmSubmitProposalInputDocument, input)).crmSubmitProposalInput,
    onSuccess: (submission) => {
      // The lock closes this editor. Mark its view stale for a future revised round, but do not
      // refetch the active editor while an optional PDF render is still running: the server rightly
      // refuses an editable wizard for locked input, which otherwise replaces progress with an error
      // before navigation to the proposal detail completes.
      void queryClient.invalidateQueries({
        queryKey: wizardKey(tenantId, submission.proposalId),
        refetchType: 'none',
      })
      // State and version changed: list and detail can refresh immediately.
      void queryClient.invalidateQueries({ queryKey: [...proposalKeys.root(tenantId), 'list'] })
      void queryClient.invalidateQueries({ queryKey: proposalKeys.detail(tenantId, submission.proposalId) })
    },
  })
}

/**
 * The label of a catalogue option the user has already seen, for a selector whose current list
 * (filtered by the search, or capped at the first page) no longer holds the chosen option. The
 * cached pages of every search and parent for this kind are read, so the answer survives the search
 * being cleared. `undefined` when no page ever held the key (for example a draft opened in a fresh
 * session); the caller then falls back to the key.
 */
export function useCachedCatalogueLabel(kind: CatalogueKind): (key: string) => string | undefined {
  const tenantId = useTenantId()
  const client = useQueryClient()
  return (key) => {
    const pages = client.getQueriesData<Array<{ key: string; label: string }>>({
      queryKey: [...proposalKeys.root(tenantId), 'catalogue', kind],
    })
    for (const [, items] of pages) {
      const hit = items?.find((i) => i.key === key)
      if (hit) return hit.label
    }
    return undefined
  }
}

/** One page of catalogue options for a selector, matched on the server by `search`. */
export function useCatalogueOptions(args: { kind: CatalogueKind; parentKey?: string; search: string; enabled: boolean }) {
  const tenantId = useTenantId()
  const { kind, parentKey, search, enabled } = args
  return useQuery({
    queryKey: [...proposalKeys.root(tenantId), 'catalogue', kind, parentKey ?? null, search] as const,
    queryFn: async () => {
      const data = await fetchGql(CrmProposalCatalogueOptionsDocument, {
        kind,
        parentKey: parentKey ?? null,
        search: search.trim() === '' ? null : search.trim(),
        first: 50,
      })
      return data.crmProposalCatalogueOptions.items
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

type PackageOption = { key: string; visaCount?: number | null }

/**
 * The visa count of the package the proposal has chosen: the `visaCount` of that package's catalogue
 * option (`crmProposalCatalogueOptions(kind: PACKAGE)`), or `null` when it is not known.
 *
 * It looks in the same list the package selector shows (the jurisdiction's packages, empty search), which
 * the selector has usually just cached, and then in any other page of PACKAGE options already seen for that
 * jurisdiction (a search the seller ran to find the package). It is read only while `enabled`, so a step with
 * no visa count to default does not ask the catalogue anything. `null` is not an error: the field then opens
 * empty, as it always did. A jurisdiction with more packages than one page, opened in a fresh session, can
 * still miss the package; the seller types the count then.
 */
export function usePackageVisaCount(args: { jurisdictionKey: string; packageKey: string; enabled: boolean }): number | null {
  const tenantId = useTenantId()
  const client = useQueryClient()
  const { jurisdictionKey, packageKey } = args
  const enabled = args.enabled && jurisdictionKey !== '' && packageKey !== ''
  const listed = useCatalogueOptions({
    kind: 'PACKAGE',
    parentKey: jurisdictionKey === '' ? undefined : jurisdictionKey,
    search: '',
    enabled,
  })
  if (!enabled) return null
  let option: PackageOption | undefined = listed.data?.find((i) => i.key === packageKey)
  if (!option) {
    const pages = client.getQueriesData<PackageOption[]>({
      queryKey: [...proposalKeys.root(tenantId), 'catalogue', 'PACKAGE', jurisdictionKey],
    })
    for (const [, items] of pages) {
      option = items?.find((i) => i.key === packageKey)
      if (option) break
    }
  }
  return typeof option?.visaCount === 'number' ? option.visaCount : null
}
